import axios, {
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { isAuthRejection } from "@/shared/authFailure.ts";
import { dropAxiosRequestSecrets } from "@/shared/axiosError.ts";
import {
  endSession,
  endSessionInPlace,
  installCrossTabSignOut,
  recordAuthenticatedSession,
  takeAuthenticatedSession,
} from "./endSession.ts";
import { isOtpRequired, isRefusal } from "@/shared/stepUp.ts";
import { OKTHX_CLIENT_HEADER, type ClientName } from "@/types/clientHeader.ts";
import { STEP_UP_PATH, stepUpHref } from "./stepUp.ts";
import { getApiOrigin, toApiMountPath } from "./utils.ts";

const apiOrigin = getApiOrigin();

// Both Vite entrypoints import this module before app code so the shared axios
// instance is configured before any feature can issue a request.
axios.defaults.baseURL = apiOrigin;
axios.defaults.headers.common["Content-Type"] = "application/json";
axios.defaults.withCredentials = true;
axios.defaults.headers.common[OKTHX_CLIENT_HEADER] = "web-app" satisfies ClientName;

/**
 * Any path the API serves, origin-relative.
 *
 * `baseURL` is the bare origin, so callers spell the `/api` mount themselves —
 * which is what keeps a missed migration a compile error rather than a runtime
 * 404, and what leaves routes outside the mount reachable at all.
 */
export type ApiPath = `/api/${"a" | "p" | "s"}/${string}` | "/api/healthz";

/**
 * Whether a failed request was aimed at our own API.
 *
 * Compares parsed origins rather than string prefixes: a raw
 * `startsWith(apiOrigin)` also matches `https://api.okthx.com.evil.example/…`,
 * and a raw `startsWith("/")` matches the protocol-relative `//evil.example/…`.
 * Resolving through `getUri` (the same `buildFullPath` the request itself uses,
 * per `axios/lib/core/Axios.js`) rather than reading `config.url` means a
 * per-call `baseURL` override is accounted for too.
 */
function ourApiPathname(config: InternalAxiosRequestConfig | undefined): string | undefined {
  if (config?.url === undefined) return undefined;
  try {
    const url = new URL(axios.getUri(config), window.location.href);
    if (url.origin !== apiOrigin) return undefined;
    if (url.pathname === "/api" || url.pathname.startsWith("/api/")) return url.pathname;
    return undefined;
  } catch {
    return undefined;
  }
}

export function isOurApiRequest(config: InternalAxiosRequestConfig | undefined): boolean {
  return ourApiPathname(config) !== undefined;
}

/**
 * Whether a request went to a route only an authenticated session can reach.
 * A success there is proof the document holds a session — the signal that
 * makes a later rejection a sign-out rather than a stranger's 401.
 */
export function isAuthenticatedApiRequest(config: InternalAxiosRequestConfig | undefined): boolean {
  return ourApiPathname(config)?.startsWith("/api/a/") === true;
}

export function shouldRedirectToAuth(error: unknown): boolean {
  if (!isAuthRejection(error) || typeof error !== "object" || error === null) return false;
  return isOurApiRequest((error as { config?: InternalAxiosRequestConfig }).config);
}

/**
 * Where a rejected session lands. The current deep link rides along so login
 * returns the user here — e.g. the approval email's one-click `?act=` action
 * (BIG-5585). Auth.tsx reads `?returnTo` and routes back after OAuth.
 */
function authUrlReturningHere(): string {
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  return `/auth?returnTo=${returnTo}`;
}

/** Record a session on any authenticated success; the response passes through. */
export function handleSuccessfulResponse<T extends AxiosResponse>(response: T): T {
  if (isAuthenticatedApiRequest(response.config)) recordAuthenticatedSession();
  return response;
}

/**
 * Reject a failed response, first ending the session here if our API has
 * declared its credential dead.
 *
 * A rejection is only a sign-out when a session existed in this browser: then
 * storage is wiped and the document torn down — reloaded in place when already
 * on the auth page, since that page must not keep the old account's state
 * either. Taking the session record before reloading is what stops the next
 * document from reloading again. Without such a record (a visitor who never
 * signed in, or the document just reloaded) there is nothing to wipe, and the
 * only action is the redirect a deep link needs.
 */
export function handleRejectedResponse(error: unknown): Promise<never> {
  dropAxiosRequestSecrets(error);
  if (shouldRedirectToAuth(error)) {
    const onAuthPage = window.location.pathname.startsWith("/auth");
    if (takeAuthenticatedSession()) {
      if (onAuthPage) endSessionInPlace();
      else endSession(authUrlReturningHere());
    } else if (!onAuthPage) {
      window.location.assign(authUrlReturningHere());
    }
  }
  // An action this account may take only after proving its number by code.
  // Send the person to prove it, then back to where they were.
  if (
    isOtpRequired(error) &&
    isOurApiRequest((error as { config?: InternalAxiosRequestConfig }).config) &&
    !window.location.pathname.startsWith(STEP_UP_PATH)
  ) {
    window.location.assign(stepUpHref(window.location.pathname + window.location.search));
  }
  // An account an emailed claim link signed in to may do nothing but prove a
  // phone; send it there, unless it is already on the way.
  if (
    isRefusal(error, "PHONE_VERIFICATION_REQUIRED") &&
    isOurApiRequest((error as { config?: InternalAxiosRequestConfig }).config) &&
    !window.location.pathname.startsWith("/onboarding")
  ) {
    window.location.assign("/onboarding/phone-number");
  }
  return Promise.reject(error);
}

axios.interceptors.response.use(handleSuccessfulResponse, handleRejectedResponse);

// Every entry point imports this module before any app code, so this is the
// one place that guarantees each tab hears a sign-out performed in another.
installCrossTabSignOut();

export { apiErrorMessage, apiErrorReason } from "@/shared/apiErrorMessage.ts";

/**
 * Whether a URL supplies its own host, either explicitly or through `//`.
 *
 * Pattern-matched rather than parsed: `URL` cannot answer this question in
 * either mode — with no base it rejects `//cdn.example/x` outright, and with one
 * it resolves that to a host of its own, collapsing the very distinction being
 * drawn here.
 */
function isAbsoluteOrProtocolRelativeUrl(url: string): boolean {
  return /^([a-z][a-z0-9+.-]*:)?\/\//i.test(url);
}

if (import.meta.env.DEV) {
  // `ApiPath` can't reject a non-rooted `"a/x"`, which an origin baseURL would
  // silently resolve against the current page path. Catch those here instead.
  axios.interceptors.request.use((config) => {
    const url = config.url ?? "";
    if (url !== "" && !isAbsoluteOrProtocolRelativeUrl(url) && !url.startsWith("/api/")) {
      throw new Error(
        `Request to "${url}" is not an origin-relative API path. Use "/api/a/…" (see ApiPath in lib/http.ts).`,
      );
    }
    return config;
  });
}

/**
 * The API client. Thin typed wrappers over the configured instance — they exist
 * to constrain paths to {@link ApiPath}; everything else is axios'.
 *
 * Requests to other origins (Google Places, GCS signed-URL uploads) deliberately
 * do not belong here — they use `fetch` so they never carry our cookies or hit
 * the 401 interceptor above.
 */
export const api = {
  get: <T = unknown>(url: ApiPath, config?: AxiosRequestConfig) => axios.get<T>(url, config),
  post: <T = unknown, TBody = unknown>(url: ApiPath, data?: TBody, config?: AxiosRequestConfig) =>
    axios.post<T>(url, data, config),
  put: <T = unknown, TBody = unknown>(url: ApiPath, data?: TBody, config?: AxiosRequestConfig) =>
    axios.put<T>(url, data, config),
  patch: <T = unknown, TBody = unknown>(url: ApiPath, data?: TBody, config?: AxiosRequestConfig) =>
    axios.patch<T>(url, data, config),
  delete: <T = unknown>(url: ApiPath, config?: AxiosRequestConfig) => axios.delete<T>(url, config),

  /**
   * POST to a server-chosen path. Separate from {@link api.post} because these
   * paths are not literals in our source — the server owns them — so they can't
   * be checked against {@link ApiPath}. Keeping them on their own method means
   * that escape hatch stays greppable rather than weakening the type.
   */
  postServerPath: <T = unknown, TBody = unknown>(
    path: string,
    data?: TBody,
    config?: AxiosRequestConfig,
  ) => axios.post<T>(toApiMountPath(path), data, config),
};

export default axios;
